import { apiService } from './apiService'
import { storageWrapper } from '../utils/storageWrapper'

/**
 * Enum Cache Service
 *
 * Provides cached access to enum data (accommodation types, densities, regions, etc.)
 * with optimized fetching via a combined endpoint.
 *
 * ## How It Works
 * - Common enums are fetched together in a single API call: `/enums/all?include=...`
 * - Multiple simultaneous requests share the same fetch (promise deduplication)
 * - Data is cached in localStorage with 1-hour TTL and shared across browser tabs
 * - In-memory cache provides fast access within the same tab
 *
 * ## Usage Best Practice
 * Always await the getter before using the data:
 *
 * ```javascript
 * // In Vue component
 * const regions = ref([])
 * const loading = ref(true)
 *
 * onMounted(async () => {
 *   regions.value = await getRegions()
 *   loading.value = false
 * })
 * ```
 *
 * ## Important Notes
 * - Cache is shared across browser tabs via localStorage
 * - Cache expires after 1 hour (TTL)
 * - Cache is cleared on login for fresh data each session
 * - notificationTypes uses original {id, name} format (not normalized)
 */

// Storage keys and TTL
const STORAGE_KEY = 'eams_enum_cache'
const TTL_MS = 60 * 60 * 1000 // 1 hour

// Cache state
const cache = {
  accommodationTypes: { data: null, loaded: false },
  densities: { data: null, loaded: false },
  durations: { data: null, loaded: false },
  regions: { data: null, loaded: false },
  clientGroups: { data: null, loaded: false },
  roomTypes: { data: null, loaded: false },
  notificationTypes: { data: null, loaded: false },
  allEnumsLoaded: false
}

// Promise for in-flight request (allows multiple callers to await the same fetch)
let allEnumsLoadingPromise = null

/**
 * Load cache from localStorage if valid (not expired)
 */
const loadFromStorage = () => {
  try {
    const stored = storageWrapper.getItem(STORAGE_KEY)
    if (!stored) return false

    const parsed = JSON.parse(stored)
    const now = Date.now()

    // Check TTL
    if (!parsed.cachedAt || (now - parsed.cachedAt) > TTL_MS) {
      storageWrapper.removeItem(STORAGE_KEY)
      return false
    }

    // Restore cache from storage
    if (parsed.data) {
      Object.keys(parsed.data).forEach(key => {
        if (cache[key]) {
          cache[key].data = parsed.data[key]
          cache[key].loaded = parsed.data[key] !== null
        }
      })
      cache.allEnumsLoaded = parsed.allEnumsLoaded || false
      return true
    }
  } catch (e) {
    console.error('Error loading enum cache from storage:', e)
    storageWrapper.removeItem(STORAGE_KEY)
  }
  return false
}

/**
 * Save cache to localStorage
 */
const saveToStorage = () => {
  try {
    const dataToStore = {
      cachedAt: Date.now(),
      allEnumsLoaded: cache.allEnumsLoaded,
      data: {}
    }

    Object.keys(cache).forEach(key => {
      if (key !== 'allEnumsLoaded' && cache[key]?.data !== undefined) {
        dataToStore.data[key] = cache[key].data
      }
    })

    storageWrapper.setItem(STORAGE_KEY, JSON.stringify(dataToStore))
  } catch (e) {
    console.error('Error saving enum cache to storage:', e)
  }
}

/**
 * Listen for changes from other tabs
 */
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    // Avoid overwriting in-memory cache from storage while a fetch is in progress
    if (event.key === STORAGE_KEY && !allEnumsLoadingPromise) {
      loadFromStorage()
    }
  })
}

// Load from storage on module initialization
loadFromStorage()

/**
 * Normalize enum option to consistent format
 */
const normalizeEnumOption = (option) => {
  if (!option || typeof option !== 'object') return null
  const rawValue = option.id ?? option.value ?? option.key ?? option.code ?? option.slug
  if (rawValue == null) return null

  const numericValue = Number(rawValue)
  const value = Number.isFinite(numericValue) ? numericValue : rawValue
  const title = option.name || option.title || option.label || String(rawValue)

  const normalized = {
    title,
    value,
  }

  // Preserve category property for grouping (used by client-groups)
  if (option.category) {
    normalized.category = option.category
  }

  return normalized
}

/**
 * Transform function for enum data - normalizes and filters
 */
const normalizeEnumList = (list) => {
  if (!Array.isArray(list)) return []
  return list
    .map(normalizeEnumOption)
    .filter(Boolean)
}

/**
 * Transform and filter accommodation types - excludes "Rooming House Unregistered"
 */
const normalizeAccommodationTypes = (list) => {
  return normalizeEnumList(list).filter(
    type => type.title !== 'Rooming House Unregistered'
  )
}

/**
 * Group client groups by category for display in select dropdown
 * Creates a flat array with header items interspersed
 */
const groupClientGroupsByCategory = (list) => {
  const groups = {}
  const categoryOrder = []

  // Group by category
  for (const item of list) {
    const category = item.category || 'Other'
    if (!groups[category]) {
      groups[category] = []
      categoryOrder.push(category)
    }
    groups[category].push(item)
  }

  // Sort categories alphabetically
  categoryOrder.sort()

  // Create flat array with headers
  const result = []
  for (const category of categoryOrder) {
    // Add header
    result.push({
      type: 'header',
      title: category,
      value: `header_${category}`, // Unique value to prevent selection
    })
    // Add items in this category
    result.push(...groups[category])
  }

  return result
}

// Enums to fetch in combined request (reduces concurrent API calls)
// notificationTypes is excluded - only needed for notification bell component
const COMBINED_ENUMS = ['accommodationTypes', 'densities', 'clientGroups', 'durations', 'regions', 'roomTypes']

/**
 * Fetch homepage enums in a single request and populate caches
 */
const fetchAllEnums = async () => {
  // Return if already loaded
  if (cache.allEnumsLoaded) {
    return
  }

  // If already loading, return existing promise (multiple callers await the same fetch)
  if (allEnumsLoadingPromise) {
    return allEnumsLoadingPromise
  }

  // Create and store the promise
  allEnumsLoadingPromise = (async () => {
    try {
      const response = await apiService.get(`/enums/all?include=${COMBINED_ENUMS.join(',')}`)

      // Populate individual caches with normalized data
      if (response.accommodationTypes) {
        cache.accommodationTypes.data = normalizeAccommodationTypes(response.accommodationTypes)
        cache.accommodationTypes.loaded = true
      }

      if (response.densities) {
        cache.densities.data = normalizeEnumList(response.densities)
        cache.densities.loaded = true
      }

      if (response.clientGroups) {
        const normalized = normalizeEnumList(response.clientGroups)
        cache.clientGroups.data = groupClientGroupsByCategory(normalized)
        cache.clientGroups.loaded = true
      }

      if (response.durations) {
        cache.durations.data = normalizeEnumList(response.durations)
        cache.durations.loaded = true
      }

      if (response.regions) {
        cache.regions.data = normalizeEnumList(response.regions)
        cache.regions.loaded = true
      }

      if (response.roomTypes) {
        cache.roomTypes.data = normalizeEnumList(response.roomTypes)
        cache.roomTypes.loaded = true
      }

      // Note: amenityTypes is handled by amenityCacheService via /Amenities/with-options
      // Note: notificationTypes is fetched on-demand (only needed for notification bell)

      cache.allEnumsLoaded = true
      saveToStorage()
    } catch (error) {
      console.error('Failed to fetch combined enums:', error?.message || error)
    } finally {
      allEnumsLoadingPromise = null
    }
  })()

  return allEnumsLoadingPromise
}

/**
 * Generic getter that loads enum data
 * - For combined enums: tries combined endpoint first, then falls back to individual
 * - For other enums (e.g., notificationTypes): fetches directly from individual endpoint
 */
const getEnumData = async (cacheKey, fallbackEndpoint, transform = normalizeEnumList) => {
  // Return cached data if already loaded
  if (cache[cacheKey].loaded) {
    return cache[cacheKey].data || []
  }

  // For combined enums, try combined endpoint first
  if (COMBINED_ENUMS.includes(cacheKey)) {
    await fetchAllEnums()
    if (cache[cacheKey].loaded) {
      return cache[cacheKey].data || []
    }
  }

  // Fetch from individual endpoint (either as fallback or primary for non-homepage enums)
  try {
    const response = await apiService.get(fallbackEndpoint)
    const data = Array.isArray(response?.data) ? response.data : (Array.isArray(response) ? response : [])
    cache[cacheKey].data = transform(data)
    cache[cacheKey].loaded = true
    saveToStorage()
  } catch (error) {
    console.error(`Failed to load ${cacheKey}:`, error?.message || error)
    return []
  }

  return cache[cacheKey].data || []
}

export const getAccommodationTypes = async () => {
  return getEnumData('accommodationTypes', '/enums/accommodation-types', normalizeAccommodationTypes)
}

export const getDensities = async () => {
  return getEnumData('densities', '/enums/densities', normalizeEnumList)
}

export const getDurations = async () => {
  return getEnumData('durations', '/enums/durations', normalizeEnumList)
}

export const getRegions = async () => {
  return getEnumData('regions', '/enums/regions', normalizeEnumList)
}

export const getClientGroups = async () => {
  return getEnumData('clientGroups', '/enums/client-groups', (list) => {
    const normalized = normalizeEnumList(list)
    return groupClientGroupsByCategory(normalized)
  })
}

export const getRoomTypes = async () => {
  return getEnumData('roomTypes', '/enums/room-types', normalizeEnumList)
}

export const getNotificationTypes = async () => {
  // Keep original format { id, name } - not used in dropdowns
  return getEnumData('notificationTypes', '/enums/notification-types', (list) => list)
}

export const clearAccommodationTypesCache = () => {
  cache.accommodationTypes.data = null
  cache.accommodationTypes.loaded = false
}

export const clearDensitiesCache = () => {
  cache.densities.data = null
  cache.densities.loaded = false
}

export const clearDurationsCache = () => {
  cache.durations.data = null
  cache.durations.loaded = false
}

export const clearRegionsCache = () => {
  cache.regions.data = null
  cache.regions.loaded = false
}

export const clearClientGroupsCache = () => {
  cache.clientGroups.data = null
  cache.clientGroups.loaded = false
}

export const clearRoomTypesCache = () => {
  cache.roomTypes.data = null
  cache.roomTypes.loaded = false
}

export const clearNotificationTypesCache = () => {
  cache.notificationTypes.data = null
  cache.notificationTypes.loaded = false
}

export const clearAllEnumCaches = () => {
  Object.keys(cache).forEach(key => {
    if (typeof cache[key] === 'object' && cache[key] !== null && 'data' in cache[key]) {
      cache[key].data = null
      cache[key].loaded = false
    }
  })
  cache.allEnumsLoaded = false
  storageWrapper.removeItem(STORAGE_KEY)
}

export const getAccommodationTypesCacheStatus = () => {
  return { loaded: cache.accommodationTypes.loaded, hasData: cache.accommodationTypes.data !== null }
}

export const getDensitiesCacheStatus = () => {
  return { loaded: cache.densities.loaded, hasData: cache.densities.data !== null }
}

export const getDurationsCacheStatus = () => {
  return { loaded: cache.durations.loaded, hasData: cache.durations.data !== null }
}

export const getRegionsCacheStatus = () => {
  return { loaded: cache.regions.loaded, hasData: cache.regions.data !== null }
}

export const getClientGroupsCacheStatus = () => {
  return { loaded: cache.clientGroups.loaded, hasData: cache.clientGroups.data !== null }
}

export const getRoomTypesCacheStatus = () => {
  return { loaded: cache.roomTypes.loaded, hasData: cache.roomTypes.data !== null }
}

export const getNotificationTypesCacheStatus = () => {
  return { loaded: cache.notificationTypes.loaded, hasData: cache.notificationTypes.data !== null }
}

// Export for preloading all enums at app startup
export const preloadAllEnums = fetchAllEnums
